import React from "react";

import { useFieldValue, useSectionToggle } from "hooks";

import { useSelector } from "react-redux";
import {
  getPrerequisiteMasterRuleChainByMasterRuleId,
  getPrerequisiteRuleChainByRuleId,
  RuleChain,
} from "containers/Settings/RuleBooks/Schema/livestockSaleSchema";
import { convertToSchemaPath } from "containers/Settings/RuleBooks/import";
import { normalisePathParts } from "containers/Settings/RuleBooks//Schema/schema";
import { getMasterRuleSetById, getRuleSetById } from "selectors";
import { Section } from "./constants";
import { GeneralSection } from "./General/GeneralSection";
import { CriteriaSection } from "./Criteria/CriteriaSection";
import { ToFromSection } from "./ToFrom/ToFromSection";
import { ValueSection } from "./Value/ValueSection";
import { PresentationFormattingSection } from "./PresentationFormatting/PresentationFormattingSection";

interface RuleFormProps {
  categories?: string[];
  billingTags: Array<{ id: string; name: string }>;
  isMasterRule?: boolean;
  ruleId?: string;
}

export function getParentPath(
  prerequisiteRuleChain: RuleChain | null,
  ruleSet: RuleSet | null,
) {
  if (prerequisiteRuleChain) {
    // The Rule has a prerequisite rule, meaning the Rule will be run on top of the datum
    // selected by the prerequisite rule (which may run atop another rule's input_field)
    // using the prerequisite Rule chain handles the complexity of determining that dataset, we can just use that
    return convertToSchemaPath(
      normalisePathParts(prerequisiteRuleChain.inputPathParts),
    );
  }
  if (ruleSet) {
    // This Rule does not have a prerequisite rule, but does have a Rule Set, in which case the Rule will be run on top of the datum selected by the RuleSet's input_field
    return convertToSchemaPath(ruleSet.input_field);
  }
  // This rule, based on the absence of a prerequisite rule and rule set, runs on the data selected by the Rule's input_field
  // There is no parent path to provide
  return null;
}

export function RuleForm(props: RuleFormProps) {
  const { categories = [], billingTags, isMasterRule = false, ruleId } = props;

  const sectionToggleState = useSectionToggle(Section.GENERAL);

  const ruleInputPath: string = useFieldValue("input_field.value");

  // Using the currently set ruleSet and first prerequisite Rule,
  // get the path to the parent datatype
  const ruleSetId = useFieldValue("rule_set_id");
  const ruleSet = useSelector(
    isMasterRule ? getMasterRuleSetById(ruleSetId) : getRuleSetById(ruleSetId),
  );
  const firstPrerequisiteRuleId =
    useFieldValue("prerequisite_rule_ids")?.[0] || null;

  const prereqChain = useSelector(
    isMasterRule
      ? getPrerequisiteMasterRuleChainByMasterRuleId(firstPrerequisiteRuleId)
      : getPrerequisiteRuleChainByRuleId(firstPrerequisiteRuleId),
  );

  const parentInputPath = getParentPath(prereqChain, ruleSet);

  const fullInputPath = normalisePathParts([parentInputPath, ruleInputPath]);

  const hasPrerequisiteRule = Boolean(firstPrerequisiteRuleId);

  return (
    <>
      <GeneralSection
        sectionToggleState={sectionToggleState}
        billingTags={billingTags}
        categories={categories}
        isMasterRule={isMasterRule}
        fullInputPath={fullInputPath}
        ruleId={ruleId}
      />
      <CriteriaSection
        hasPrerequisiteRule={hasPrerequisiteRule}
        sectionToggleState={sectionToggleState}
        parentInputPath={parentInputPath}
      />
      <ValueSection
        sectionToggleState={sectionToggleState}
        isMasterRule={isMasterRule}
        parentInputPath={parentInputPath}
        ruleInputPath={ruleInputPath}
        showPrerequisiteRuleSource={hasPrerequisiteRule}
      />
      <ToFromSection
        sectionToggleState={sectionToggleState}
        ruleInputPath={ruleInputPath}
        parentInputPath={parentInputPath}
        showPrerequisiteRuleSource={hasPrerequisiteRule}
      />
      <PresentationFormattingSection sectionToggleState={sectionToggleState} />
    </>
  );
}
